import { GAME } from "../config/gameConfig";
import type { Game } from "../game/Game";

type HorizontalKey = "ArrowLeft" | "ArrowRight";

export class KeyboardInput {
  private horizontal: HorizontalKey | null = null;
  private pressedAt = 0;
  private repeatedAt = 0;
  private softDropHeld = false;
  private softDropRepeatedAt = 0;
  private downKeys = new Set<string>();
  private secretProgress = 0;
  private secretTypedAt = 0;
  private readonly attachedWindows = new WeakSet<Window>();

  constructor(private readonly game: Game, private readonly canRestart: () => boolean = () => true, private readonly canControl: () => boolean = () => true) {
    this.attachWindow(window);
  }

  attachWindow(target: Window): void {
    if (this.attachedWindows.has(target)) return;
    this.attachedWindows.add(target);
    target.addEventListener("keydown", this.onKeyDown, { passive: false });
    target.addEventListener("keyup", this.onKeyUp, { passive: false });
    target.addEventListener("blur", () => this.reset());
  }

  update(now: number): void {
    if (this.game.phase !== "playing") return;
    if (
      this.horizontal &&
      now - this.pressedAt >= GAME.dasMs &&
      now - this.repeatedAt >= GAME.arrMs
    ) {
      this.game.moveHorizontal(this.horizontal === "ArrowLeft" ? -1 : 1);
      this.repeatedAt = now;
    }
    if (this.softDropHeld && now - this.softDropRepeatedAt >= GAME.arrMs) {
      this.game.softDrop();
      this.softDropRepeatedAt = now;
    }
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (!this.canControl()) return;
    if ((event.target as HTMLElement)?.closest("input, button, select, textarea, summary, [contenteditable], [role=textbox]")) return;
    if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) { this.secretProgress = 0; return; }
    const code = event.code;
    if (["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp", "Space", "KeyZ", "KeyX", "Escape"].includes(code)) {
      event.preventDefault();
    }
    if (event.repeat || this.downKeys.has(code)) return;
    this.downKeys.add(code);
    const now = performance.now();
    const sequence = "TETRIS";
    if (now - this.secretTypedAt > 5000) this.secretProgress = 0;
    const letter = /^Key[A-Z]$/.test(code) ? code.slice(3) : "";
    this.secretProgress = letter === sequence[this.secretProgress] ? this.secretProgress + 1 : letter === sequence[0] ? 1 : 0;
    this.secretTypedAt = now;
    if (this.secretProgress === sequence.length) {
      this.reset();
      if (this.canRestart()) this.game.start("alternate");
      return;
    }

    if (code === "Space") {
      if (this.game.phase === "title" || (this.game.phase === "game-over" && this.canRestart())) this.game.start(this.game.mode);
      else this.game.switchLayer();
      return;
    }
    if (code === "Escape") {
      this.game.togglePause();
      return;
    }
    if (this.game.phase !== "playing") return;

    if (code === "ArrowLeft" || code === "ArrowRight") {
      this.horizontal = code;
      this.pressedAt = performance.now();
      this.repeatedAt = this.pressedAt;
      this.game.moveHorizontal(code === "ArrowLeft" ? -1 : 1);
    } else if (code === "ArrowDown") {
      this.softDropHeld = true;
      this.softDropRepeatedAt = performance.now();
      this.game.softDrop();
    }
    else if (code === "ArrowUp") this.game.hardDrop();
    else if (code === "KeyZ") this.game.rotate(-1);
    else if (code === "KeyX") this.game.rotate(1);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.downKeys.delete(event.code);
    if (event.code === this.horizontal) this.horizontal = null;
    if (event.code === "ArrowDown") this.softDropHeld = false;
  };

  private reset(): void {
    this.downKeys.clear();
    this.horizontal = null;
    this.softDropHeld = false;
    this.secretProgress = 0;
    this.secretTypedAt = 0;
  }
}
